// Client for the home server's compute API: jobs with SSE progress and ETA.

export type JobStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

export interface Submitted {
  job_id: string;
  status: JobStatus;
  queue_position: number | null;
  eta_ms: number;
  estimated_run_ms: number;
  cached: boolean;
}

export interface JobView {
  id: string;
  kind: string;
  status: JobStatus;
  queue_position?: number;
  progress: number;
  eta_ms: number;
  estimated_run_ms: number;
  elapsed_ms: number;
  result_url?: string;
  error?: string;
  cached: boolean;
  meta?: Record<string, unknown>;
}

export interface Estimate {
  estimated_run_ms: number;
  eta_ms: number;
  queue_length: number;
  workers: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function check(r: Response): Promise<Response> {
  if (r.ok) return r;
  let code = 'http_error';
  let message = `HTTP ${r.status}`;
  try {
    const body = (await r.json()) as { error?: { code: string; message: string } };
    if (body.error) ({ code, message } = body.error);
  } catch {
    /* not JSON */
  }
  throw new ApiError(r.status, code, message);
}

export function form(params: unknown, files: Record<string, Blob | undefined> = {}): FormData {
  const f = new FormData();
  f.set('params', JSON.stringify(params));
  for (const [name, blob] of Object.entries(files)) {
    if (blob) f.set(name, blob, blob instanceof File ? blob.name : `${name}.bin`);
  }
  return f;
}

export async function submit(path: string, body: FormData, signal?: AbortSignal): Promise<Submitted> {
  const r = await check(await fetch(`/api/${path}`, { method: 'POST', body, signal }));
  return (await r.json()) as Submitted;
}

/** POST that may answer synchronously with a file (e.g. small stereograms). */
export async function submitOrFile(path: string, body: FormData, signal?: AbortSignal): Promise<Submitted | Blob> {
  const r = await check(await fetch(`/api/${path}`, { method: 'POST', body, signal }));
  return r.status === 202 ? ((await r.json()) as Submitted) : await r.blob();
}

export async function estimate(params: Record<string, string | number>, signal?: AbortSignal): Promise<Estimate> {
  const q = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]));
  const r = await check(await fetch(`/api/estimate?${q}`, { signal }));
  return (await r.json()) as Estimate;
}

export async function cancel(id: string): Promise<void> {
  await fetch(`/api/jobs/${id}`, { method: 'DELETE' }).catch(() => {});
}

export async function result(view: JobView): Promise<Blob> {
  const r = await check(await fetch(view.result_url ?? `/api/jobs/${view.id}/result`));
  return r.blob();
}

/**
 * Follows a job over SSE until it finishes. Resolves with the final view,
 * rejects on failure; aborting the signal cancels the job on the server.
 */
export function follow(id: string, onUpdate: (v: JobView) => void, signal?: AbortSignal): Promise<JobView> {
  return new Promise((resolve, reject) => {
    const es = new EventSource(`/api/jobs/${id}/events`);
    let settled = false;
    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      es.close();
      signal?.removeEventListener('abort', onAbort);
      fn();
    };
    const onAbort = () => {
      void cancel(id);
      done(() => reject(new DOMException('aborted', 'AbortError')));
    };
    signal?.addEventListener('abort', onAbort);
    es.addEventListener('status', (ev) => {
      const v = JSON.parse((ev as MessageEvent<string>).data) as JobView;
      onUpdate(v);
      if (v.status === 'done') done(() => resolve(v));
      else if (v.status === 'failed' || v.status === 'cancelled')
        done(() => reject(new ApiError(500, v.status, v.error ?? v.status)));
    });
    es.onerror = () => {
      // The stream closes after the final event; anything else is a network error.
      if (!settled) {
        fetch(`/api/jobs/${id}`)
          .then((r) => r.json() as Promise<JobView>)
          .then((v) => {
            onUpdate(v);
            if (v.status === 'done') done(() => resolve(v));
            else if (v.status === 'failed' || v.status === 'cancelled')
              done(() => reject(new ApiError(500, v.status, v.error ?? v.status)));
          })
          .catch((e) => done(() => reject(e instanceof Error ? e : new Error(String(e)))));
      }
    };
  });
}

/** Submits, follows and downloads the result in one go. */
export async function runJob(
  path: string,
  body: FormData,
  onUpdate: (v: Partial<JobView> & { eta_ms: number }) => void,
  signal?: AbortSignal,
): Promise<{ view: JobView; blob: Blob }> {
  const s = await submit(path, body, signal);
  onUpdate({
    status: s.status,
    eta_ms: s.eta_ms,
    estimated_run_ms: s.estimated_run_ms,
    queue_position: s.queue_position ?? undefined,
    progress: 0,
  });
  const view = await follow(s.job_id, onUpdate, signal);
  return { view, blob: await result(view) };
}
